/**
 * Protected route wrapper for authenticated-only access
 * Prevents unauthenticated users from accessing protected pages
 *
 * @module ProtectedRoute
 */

import { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { logger } from '../../utils/logger'
import { useAuth } from '../../hooks/useAuth'
import { ROUTES } from '../../utils/constants'

/**
 * Props for ProtectedRoute component
 */
interface ProtectedRouteProps {
  children: ReactNode
}

/**
 * Protected route component
 * Wraps protected pages and handles authentication checks
 * Shows loading spinner while auth is being verified
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const auth = useAuth()

  logger.debug(`ProtectedRoute: rendering`, {
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    userId: auth.user?.id,
  })

  // Show loading state while checking authentication
  if (auth.isLoading) {
    logger.debug(`ProtectedRoute: showing loading state`)
    return (
      <div className="flex items-center justify-center min-h-screen bg-walrus-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-walrus-silver mx-auto mb-4"></div>
          <p className="text-walrus-silver">Loading...</p>
        </div>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!auth.isAuthenticated) {
    logger.warn(`ProtectedRoute: user not authenticated, redirecting to login`)
    return <Navigate to={ROUTES.LOGIN} replace />
  }

  // TODO: Consider adding role-based access control here
  // Could check for specific roles if needed

  logger.debug(`ProtectedRoute: access granted, rendering children`)
  return <>{children}</>
}
